import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  RiSearchLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiEdit2Line,
  RiDeleteBin6Line,
} from "react-icons/ri";

import {
  getSubCategories,
  deleteSubCategory,
} from "../services/subCategoryService";
import { getCategories } from "../services/categoryService";
import "./OutletsList.css";

export const SubCategoryListLayer = () => {
  const [subCategories, setSubCategories] = useState([]);
  const [categoriesMap, setCategoriesMap] = useState({});
  const [loading, setLoading] = useState(true);

  // 🔍 search
  const [search, setSearch] = useState("");

  // 📄 pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const navigate = useNavigate();

  useEffect(() => {
    loadCategories();
    loadSubCategories();
  }, []);

  /* LOAD CATEGORIES (UNCHANGED) */
  const loadCategories = async () => {
    try {
      const res = await getCategories();

      if (res?.status === "success") {
        const map = {};
        (res.categories || []).forEach((cat) => {
          map[cat.id] = cat.name;
        });
        setCategoriesMap(map);
      }
    } catch {
      toast.error("Failed to load categories");
    }
  };

  /* LOAD SUB CATEGORIES (UNCHANGED) */
  const loadSubCategories = async () => {
    try {
      setLoading(true);
      const res = await getSubCategories();

      if (res?.status === "success") {
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

        setSubCategories(list);
      } else {
        toast.error(res.message || "Failed to load sub categories");
      }
    } catch {
      toast.error("API Error while loading sub categories");
    } finally {
      setLoading(false);
    }
  };

  /* DELETE (UNCHANGED) */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sub category?"))
      return;

    try {
      const res = await deleteSubCategory(id);

      if (res?.status === "success") {
        toast.success("Sub Category deleted");
        loadSubCategories();
      } else {
        toast.error(res.message || "Delete failed");
      }
    } catch {
      toast.error("API Error while deleting");
    }
  };

  // 🔍 SEARCH (UI only)
  const filteredData = useMemo(() => {
    return subCategories.filter((s) =>
      `${s.sub_category_name} ${categoriesMap[s.category_id] || ""}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [subCategories, search, categoriesMap]);

  // 📄 PAGINATION (UI only)
  const effectivePageSize =
    pageSize === "all" ? filteredData.length : pageSize;

  const totalPages =
    effectivePageSize === 0
      ? 1
      : Math.ceil(filteredData.length / effectivePageSize);

  const paginatedData =
    pageSize === "all"
      ? filteredData
      : filteredData.slice(
          (currentPage - 1) * effectivePageSize,
          currentPage * effectivePageSize
        );

  return (
    <div className="ul-page">
      {/* HEADER */}
      <div className="ul-header">
        <h4>Sub Category List</h4>
        <Link to="/sub-category" className="ul-add-btn">
          + Add Sub Category
        </Link>
      </div>

      {/* FILTER BAR */}
      <div className="ul-filters">
        <div className="ul-search">
          <RiSearchLine />
          <input
            placeholder="Search sub category..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="ul-pagesize">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(
                e.target.value === "all"
                  ? "all"
                  : Number(e.target.value)
              );
              setCurrentPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value="all">All</option>
          </select>
          <span>entries</span>
        </div>
      </div>

      {/* TABLE */}
      <div className="ul-table-wrapper">
        <table className="ul-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Sub Category Name</th>
              <th>Category Name</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center text-muted">
                  No sub categories found
                </td>
              </tr>
            ) : (
              paginatedData.map((item, index) => (
                <tr key={item.id}>
                  <td>
                    {pageSize === "all"
                      ? index + 1
                      : (currentPage - 1) * effectivePageSize + index + 1}
                  </td>
                  <td>{item.sub_category_name}</td>
                  <td>{categoriesMap[item.category_id] || "N/A"}</td>
                  <td className="ul-actions">
                    <button
                      className="ul-btn edit"
                      onClick={() =>
                        navigate(`/edit-sub-category/${item.id}`)
                      }
                    >
                      <RiEdit2Line />Edit
                    </button>
                    <button
                      className="ul-btn delete"
                      onClick={() => handleDelete(item.id)}
                    >
                      <RiDeleteBin6Line /> Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {pageSize !== "all" && totalPages > 1 && (
        <div className="ul-pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <RiArrowLeftSLine />
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              className={currentPage === i + 1 ? "active" : ""}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            <RiArrowRightSLine />
          </button>
        </div>
      )}
    </div>
  );
};
